import type { CopyKey } from './i18n';
/* `.js`, like `chat/suggest.ts` imports `./kinds.js`: this module is compiled for plain Node
   as well as for the bundler, and Node's ESM resolver does not guess extensions. */
import { ROUTES } from './routes.js';

/**
 * Where the app can be navigated to, and what each of those places is called in CSS.
 *
 * **Every path here comes from `ROUTES`, and the type is what enforces it.** `Path` is the
 * union of the literal strings in that table, so a section spelling its own address — even a
 * correct one — is a compile error rather than a menu entry that leads nowhere. That is not a
 * hypothetical: this file was written with `/investigacion` and `/ajustes` typed into it, F-142
 * renamed both a branch away, and the two would have met in `main` with the menu still working
 * (React Router would have matched nothing and rendered the 404) and the desktop layout for
 * those screens silently gone.
 *
 * The second column is the reason this file exists. F-136 gives the stage a `data-screen`
 * attribute so the stylesheet can say something per screen — how wide the conversation may
 * run, which screens may put their panels two-up — and the obvious way to write that is a
 * second map from path to name. Two lists of the same routes drift the first time a route is
 * renamed, and the drift is silent in the worst way this project has a name for: the attribute
 * falls back to `page`, every rule keyed on that screen stops matching, and nothing anywhere
 * reports an error. The layout simply goes back to being a phone on a desk.
 *
 * So the screen name lives on the section, `screenOf` is the only way to ask, and
 * `nav.test.ts` asserts both halves: that every destination has a name, and that every
 * destination is an address `routes.ts` actually publishes.
 *
 * React-free on purpose: `tsconfig.test.json` compiles this file for plain Node, which is what
 * lets all of that be asserted without a DOM.
 */
export type Path = (typeof ROUTES)[keyof typeof ROUTES];

export type Section = {
  readonly to: Path;
  readonly label: CopyKey;
  /**
   * What `styles.css` calls this screen. English, and deliberately not the path: a stylesheet
   * cannot ask the router, and a selector spelling `/settings` would be a third place the
   * address is written down — which is what F-142 has just finished removing.
   */
  readonly screen: string;
};

/**
 * The conversation.
 *
 * **It used to be "not a section", and that sentence was the old shell's whole shape.** There
 * was one menu, the chat sat above it, and a copy of the chat rode in the bar as a button. It
 * is the third entry of `PLAN_SECTIONS` below now, because the conversation is a property of one
 * plan exactly as its progress and its curriculum are: a chat with no plan behind it has nothing
 * to ask about. The button went rather than sitting a centimetre above an entry saying the same
 * word.
 *
 * **What that cost, stated because the first draft of this comment claimed the opposite.** The
 * old button was keyed on `signedIn` and not on the screen, so the chat was one tap from
 * *everywhere* a signed-in learner could be — the console, the unsubscribe landing and the 404
 * included. The bar is drawn only where `showsPlanNav` is true, so on those three the
 * conversation is now the wordmark and then the landing redirect: two taps rather than one.
 * That is accepted rather than overlooked — none of the three is a screen a learner navigates
 * *from*, and `NotFound` carries its own link home — and it is the price of the hamburger
 * holding only global things, which is what was asked for.
 *
 * `/new-plan` is a fourth screen without the bar since F-163, and a deliberate one rather than a
 * consequence: a learner stating a plan has not got one to converse about.
 *
 * Kept as its own export because `PLAN_SECTIONS` names it and `nav.test.ts` asserts it is the
 * bar's third entry; nothing else imports it since the two `App.tsx` sites went.
 */
export const CHAT: Section = { to: ROUTES.chat, label: 'nav.chat', screen: 'chat' };

/**
 * The three screens that are about one plan, in the order the horizontal bar draws them.
 *
 * Leo, 2026-09-20: *"a horizontal menu below the logo and plan selector is the navigation for
 * the plan, which has plan, progress and chat as options."* That sentence fixes the order as
 * well as the membership, and the order is the plan's own life read left to right: what is
 * being learnt, how far through it you are, and the teaching itself.
 *
 * **These are exactly the paths `sectionHref` carries `?o=` to**, which is the isolation
 * mechanism rather than a coincidence — `nav.test.ts` asserts the two lists are the same set,
 * and it has to, because neither direction of a disagreement reports anything. A screen in this
 * bar that `PLAN_SCREENS` did not know about would draw a plan-scoped link that silently
 * dropped the plan, landing the learner on the API's default under a bar claiming otherwise; a
 * plan screen missing from this bar would be reachable only by typing its address.
 */
export const PLAN_SECTIONS: readonly Section[] = [
  { to: ROUTES.plan, label: 'nav.plan', screen: 'plan' },
  { to: ROUTES.progress, label: 'nav.progress', screen: 'progress' },
  CHAT,
];

/**
 * What is not about one plan, which is all the hamburger holds now.
 *
 * Leo, same message: *"Everything else in the ui should be constrained to the plan, except for
 * the global notification settings, profile and similars. The hamburger menu should only show
 * those global options."*
 *
 * **The reach window and the notification switch must never drift into the plan scope**, and
 * that is the owner's own earlier decision rather than a layout preference — *"Messaging
 * settings is global, I define globally, NOT per objective"* — with per-plan reminder
 * escalation depending on there being one schedule to escalate inside. `sectionHref` already
 * refuses these two a `?o=`; this list is the other half of the same statement, and it is what
 * makes the refusal visible in the interface rather than only in the addresses.
 */
export const GLOBAL_SECTIONS: readonly Section[] = [
  { to: ROUTES.settings, label: 'nav.config', screen: 'config' },
  { to: ROUTES.profile, label: 'nav.profile', screen: 'profile' },
];

/**
 * Every section of the app, whichever navigation draws it.
 *
 * **It exists for `screenOf` and for the assertions, and no longer for a menu** — nothing
 * renders all of it, which is the whole of this change. Two lists partitioning one set is what
 * keeps the split checkable: `nav.test.ts` asserts the partition is exact, so a section added to
 * neither half fails a test rather than becoming a screen the stylesheet has no name for and
 * nothing navigates to.
 *
 * **There were five entries and two of them were one screen.** *My plan* and *Research and
 * data* were two menu entries for one objective — the first showed what it had become, the
 * second what it was built from — so a learner wondering what was read about a subject had to
 * know that a second section existed and that this was the sort of thing it held. F-141 merged
 * the screens; `Research and data` survives as the name of a section *within* the plan rather
 * than as a destination beside it, which is where the distinction was always true: research is
 * a property of a plan, not a sibling of one.
 */
export const SECTIONS: readonly Section[] = [...PLAN_SECTIONS, ...GLOBAL_SECTIONS];

/**
 * Whether the plan selector belongs above this screen.
 *
 * **It used to answer for the bar underneath it as well, and since 2026-09-20 it does not.**
 * `/new-plan` gets the selector and not the bar — see `showsPlanNav`, which is where that
 * difference is argued — so the two questions are two functions, with this one still holding the
 * list and the gate.
 *
 * **Positively listed, and that is the decision.** The obvious spelling is a list of exclusions
 * — not the first run, not the console, not the sign-in screen — and a list of exclusions is one
 * somebody forgets to add to: the next route mounted outside the app's own five would arrive
 * wearing a plan selector, and nothing would report it.
 *
 * Three of the screens it excludes are the ones that matter, and each is real:
 *
 * - **`/welcome`**, because a learner mid-first-run must not meet a selector for a plan they
 *   have not finished setting up. `gated` covers that learner wherever else they are, and this
 *   covers the one address the gate deliberately lets through — so neither the gate reading
 *   `unknown` for the round trip before the facts land, nor a redirect in flight, can put the
 *   chrome on **that** screen.
 *
 *   **The word "that" is doing work, and a review of the first draft is why it is there.**
 *   `gateIsClosed` is false for `unknown` and `FirstRunGate` renders its children rather than
 *   waiting — deliberately, because a configured learner must not watch the app hesitate on
 *   every cold start. So a gated learner who cold-starts on `/plan` does see the bar for the
 *   one round trip before they are redirected. The selector does not appear with it (the plans
 *   have not been read either), and `/welcome` itself never gets either. Waiting on `unknown`
 *   would trade that flash for a header that arrives late and pushes every screen down after
 *   it has painted, for everybody, on every cold start — which is the worse of the two.
 * - **`/admin/*`**, the operator console: a ten-column table about other people's accounts,
 *   with no plan of its own to be scoped to.
 * - **`/unsubscribe`**, mounted with no session guard at all because somebody stopping our mail
 *   must not have to sign in. A signed-in operator opening one would otherwise get the app's
 *   chrome around a page that is not the app.
 */
export function showsPlanChrome(input: PlanChrome): boolean {
  if (!input.signedIn || input.gated) return false;
  /*
   * **`/new-plan` is the one address here that is not a section**, and it is in this list rather
   * than excluded from it because the selector is how a learner *leaves* that screen. Stating a
   * plan is not inside any plan, so nothing on it belongs to one — but a screen with no way back
   * is a trap, and the owner asked for the way back to be where the plans are: *"going back to any
   * existing plan fills that context with that plan only."* The selector also says where they are
   * (see `Selector`'s `creating`), which is the other half of the request.
   */
  if (input.pathname === ROUTES.newPlan) return true;
  return SECTIONS.some((section) => section.to === input.pathname);
}

/** The facts either of these two decisions is taken on. */
export interface PlanChrome {
  /** Whether the app is drawing signed-in chrome at all. See `showsSignedInChrome`. */
  signedIn: boolean;
  /** `gateIsClosed(stage)` — the first run owns the whole screen while this is true. */
  gated: boolean;
  pathname: string;
}

/**
 * Whether the plan's own three screens are drawn under the selector.
 *
 * **Everywhere the chrome is, except while a plan is being stated**, and that exception is the
 * whole reason this is a second function rather than a second reading of the first.
 *
 * **Hidden, and not shown disabled.** Three arguments, in the order they decided it:
 *
 * 1. **A disabled bar is not honest, it is stale.** `sectionHref(path, undefined)` returns a bare
 *    path, so a bar drawn here would link to `/progress` and `/chat` with no plan on them — which
 *    lands on whatever the API defaults to. That is one plan's progress reached from the screen
 *    where a *different* plan is being written, which is the cross-plan leak this change exists to
 *    remove, arriving through the one control that survived it.
 * 2. **There is nothing to disable.** A disabled control says *this exists and you may not use it
 *    yet*; these three are not waiting on a permission, they are about an object that does not
 *    exist. The screen is not a plan with its navigation switched off — it is not a plan.
 * 3. **The arithmetic stays exact for free.** `--bar-h` is `--bar-row-h + --plan-nav-h +
 *    --notice-h`, and `--plan-nav-h` is nought by default and one row under
 *    `body:has(.plan-nav)`. Not rendering the bar leaves that selector unmatched, so the header
 *    is one row shorter and the chat's box — `--app-h` minus `--bar-h` — recomputes correctly
 *    with nothing in `styles.css` changed and nothing measured in JavaScript. A *disabled* bar
 *    would still occupy the row, which would be a header claiming a height for a control that
 *    does nothing. See `PlanChrome.tsx` and the `--plan-nav-h` note in `styles.css`.
 *
 * Derived from `showsPlanChrome` rather than written out again, so the gate, the sign-out and the
 * positive list are one decision. Two independently maintained lists of the same screens is what
 * `nav.ts` exists to stop.
 */
export function showsPlanNav(input: PlanChrome): boolean {
  return showsPlanChrome(input) && input.pathname !== ROUTES.newPlan;
}

/**
 * What to put on `main.stage`'s `data-screen` for a path.
 *
 * `page` for everything else, and that covers more than a 404: the front page, the sign-in
 * screen, the unsubscribe landing and the console all land there. None of them wants a rule of
 * its own — they are a single panel or a piece of marketing, and the shell's defaults are
 * already right for both. **The console is the one that has to be checked when a default
 * changes**, because it is a ten-column table wearing a screen name that means "nothing
 * special"; `console.css` widens the page for it through `--page-w` and nothing keyed on
 * `data-screen` may narrow it again.
 */
export function screenOf(pathname: string): string {
  /* No special case for the chat any more: it is an entry of `PLAN_SECTIONS` and therefore of
     `SECTIONS`, so the lookup below finds it. A branch left in front of this list is how one of
     the two spellings comes to answer for a route the other no longer holds. */
  return SECTIONS.find((section) => section.to === pathname)?.screen ?? 'page';
}
